import React, { useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useQuery } from 'react-query';
import Pagination from '../../../components/Pagination';
import Table from '../../../components/Table';
import { convertToDollarString, formatDate } from '../../../helpers';

import { adminAPI } from '../../../api';

import EditIcon from '../../../../../../assets/images/reskin-images/icon--edit.svg';
import EyeIcon from '../../../../../../assets/images/reskin-images/icon--eye.svg';
import BinIcon from '../../../../../../assets/images/reskin-images/icon--bin.svg';
import SendIcon from '../../../../../../assets/images/reskin-images/icon--send.svg';
import useModal from '../../../context/ModalContext';
import DestroyModal from './DestroyModal';
import ResendConfirmationModal from './ResendConfirmationModal';
import useAuth from '../../../context/AuthContext';

const UnconfirmedUsersTable = ({
  searchQuery,
  filter,
  items,
  onPaginationChange,
  unconfirmedUsers,
  setUnconfirmedUsers,
  setOpenSideActionBoard,
  checkedAllUsers,
  handleSelectAllUsers,
  totalUsers,
}) => {
  const { isTenant } = useAuth();

  const [currentPage, setCurrentPage] = useState(
    items?.pagination?.current_page || 1,
  );

  useEffect(() => {
    onPaginationChange(currentPage);
  }, [currentPage]);

  const columns = [
    {
      label: 'Name',
      field: 'name',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '25%',
    },
    {
      label: 'Email',
      field: 'email',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '35%',
    },
    {
      label: 'Access Type',
      field: 'access_type',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Cohorts',
      field: 'cohorts',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Signup Date',
      field: 'signupDate',
      textAlign: 'center',
      width: '20%',
    },
  ];

  const { setContent } = useModal();

  const reconstructorFn = (user) => {
    const {
      id,
      email,
      created_at: createdAt,
      profile: { first_name: firstName, last_name: lastName },
      discounted_cents: discountedCents,
      application,
      access_type,
      cohorts,
    } = user;

    const actionButtons = (
      <div className="flex h-full items-center shrink-0">
        <button
          type="button"
          aria-label="View unconfirmed user"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg mr-2 hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoard({
              type: 'Unconfirmed Users',
              action: 'view',
              ids: [id],
            })
          }
        >
          <SVG src={EyeIcon} />
        </button>
        <button
          type="button"
          aria-label="Edit unconfirmed user"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg mr-2 hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoard({
              type: 'Unconfirmed Users',
              action: 'edit',
              ids: [id],
            })
          }
        >
          <SVG src={EditIcon} />
        </button>
        <button
          type="button"
          title="Resend confirmation email"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
          onClick={() =>
            setContent({
              content: (
                <ResendConfirmationModal
                  items={[{ id, email }]}
                  setItems={setUnconfirmedUsers}
                  checkedAllUsers={false}
                  totalUsers={1}
                  filter={filter}
                  searchQuery={searchQuery}
                  handleSelectAllUsers={handleSelectAllUsers}
                />
              ),
            })
          }
        >
          <SVG src={SendIcon} />
        </button>
      </div>
    );

    return {
      id,
      name: (
        <div className="flex flex-no-wrap whitespace-no-wrap items-center justify-between">
          {firstName} {lastName} {isTenant ? '' : actionButtons}
        </div>
      ),
      email,
      application: application ?? '---',
      access_type: access_type ?? '---',
      cohorts:
        cohorts && cohorts.length > 0
          ? cohorts.map((cohort, index) => (
              <React.Fragment key={cohort.id}>
                {cohort.name}
                {index < cohorts.length - 1 && <br />}
              </React.Fragment>
            ))
          : '---',
      signupDate: formatDate(createdAt),
    };
  };

  const editButtonLabel = unconfirmedUsers?.length >= 2 ? 'Users' : 'User';

  return (
    <div>
      <div className="mb-4 ml-10 flex items-center">
        <h2 className="font-semibold">Unconfirmed Users</h2>
        {unconfirmedUsers?.length ? (
          <button
            onClick={() =>
              setContent({
                content: (
                  <ResendConfirmationModal
                    items={unconfirmedUsers}
                    setItems={setUnconfirmedUsers}
                    checkedAllUsers={checkedAllUsers}
                    totalUsers={totalUsers}
                    filter={filter}
                    searchQuery={searchQuery}
                    handleSelectAllUsers={handleSelectAllUsers}
                  />
                ),
              })
            }
            type="button"
            className="flex items-center font-semibold bg-purple-100 rounded-lg py-3 px-4 ml-8 hover:bg-purple-lightest"
          >
            <SVG src={SendIcon} className="mr-2" />
            Resend confirmation email to{' '}
            {checkedAllUsers
              ? totalUsers.toLocaleString('en-US')
              : unconfirmedUsers.length}{' '}
            selected
          </button>
        ) : null}
        {unconfirmedUsers?.length >= 1 ? (
          <button
            onClick={() =>
              setOpenSideActionBoard({
                type: 'Unconfirmed Users',
                action: 'edit',
                ids: unconfirmedUsers,
                checkedAllUsers: checkedAllUsers,
                totalUsers: totalUsers,
              })
            }
            type="button"
            className="flex items-center font-semibold bg-purple-100 rounded-lg py-3 px-4 ml-8 hover:bg-purple-lightest"
          >
            <SVG src={EditIcon} className="mr-2" />
            Edit{' '}
            {checkedAllUsers
              ? totalUsers.toLocaleString('en-US')
              : unconfirmedUsers.length}{' '}
            {editButtonLabel}
          </button>
        ) : null}
        {unconfirmedUsers?.length ? (
          <button
            type="button"
            className="flex items-center font-semibold hover:bg-red-lighter rounded-lg p-3 ml-4"
            onClick={() => {
              setContent({
                content: (
                  <DestroyModal
                    items={unconfirmedUsers}
                    type="users"
                    queryListId="unconfirmedUsers"
                    setItems={setUnconfirmedUsers}
                    checkedAllUsers={checkedAllUsers}
                    totalUsers={totalUsers}
                    filter={filter}
                    searchQuery={searchQuery}
                    handleSelectAllUsers={handleSelectAllUsers}
                  />
                ),
              });
            }}
          >
            <SVG src={BinIcon} />
          </button>
        ) : null}
      </div>

      {!items?.isLoading && (
        <Table
          id="unconfirmed_users"
          data={items?.users}
          columns={columns}
          reconstructorFn={reconstructorFn}
          checkedItems={unconfirmedUsers}
          setCheckedItems={setUnconfirmedUsers}
          totalCount={items?.pagination?.total_count}
          onSelectAllRecords={handleSelectAllUsers}
          checkedAllUsers={checkedAllUsers}
        />
      )}
      <Pagination
        currentPage={currentPage}
        totalPage={items?.pagination?.total_pages}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
};

export default UnconfirmedUsersTable;
